<template>
  <div class="reschedule-options-editor">
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- 編輯按鈕區 (可由 hideEditControls prop 隱藏) -->
    <div v-if="!hideEditControls" class="d-flex justify-end mb-4">
      <v-btn
        v-if="!isEditing"
        outlined
        color="primary"
        class="rounded-lg"
        @click="startEditing"
      >
        <v-icon left small>mdi-pencil</v-icon>
        {{ $t('GENERAL.edit') }}
      </v-btn>
      <template v-else>
        <v-btn
          text
          class="mr-2 rounded-lg"
          @click="cancelEditing"
        >
          {{ $t('GENERAL.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          class="rounded-lg"
          @click="saveOptions"
        >
          {{ $t('GENERAL.save') }}
        </v-btn>
      </template>
    </div>

    <!-- 改約原因卡片 -->
    <v-card class="mb-6" elevation="1" rounded="lg">
      <v-card-text class="pa-6">
        <div class="d-flex justify-space-between align-center mb-2">
          <h3 class="text-subtitle-1 font-weight-bold">
            {{ $t('RescheduleSettings.reasonTitle') }}
          </h3>
          <div class="d-flex align-center">
            <v-chip
              v-if="isReasonsUsingDefault && !isEditing"
              small
              color="grey lighten-2"
              text-color="grey darken-1"
            >
              <v-icon left small>mdi-information-outline</v-icon>
              {{ $t('RescheduleSettings.usingSystemDefault') }}
            </v-chip>
            <v-btn
              v-if="isEditing && !isReasonsUsingDefault && editableReasons.length > 0"
              text
              small
              color="grey"
              @click="clearOptions('reasons')"
            >
              {{ $t('RescheduleSettings.resetToDefault') }}
            </v-btn>
          </div>
        </div>
        <p class="text-body-2 grey--text text--darken-1 mb-4">
          {{ $t('RescheduleSettings.reasonDesc') }}
        </p>

        <draggable
          v-model="editableReasons"
          :disabled="!isEditing"
          handle=".drag-handle"
          animation="200"
          class="options-list"
        >
          <transition-group type="transition" name="flip-list">
            <div
              v-for="(reason, index) in editableReasons"
              :key="reason.id || `reason-${index}`"
              class="option-item d-flex align-center pa-3 mb-2 rounded-lg"
              :class="{ 'editing': isEditing }"
            >
              <v-icon
                v-if="isEditing"
                class="drag-handle mr-3"
                color="grey"
              >
                mdi-drag
              </v-icon>
              <v-icon
                v-else
                class="mr-3"
                color="grey lighten-1"
              >
                mdi-circle-small
              </v-icon>

              <v-text-field
                v-if="isEditing"
                v-model="reason.text"
                :placeholder="$t('RescheduleSettings.inputPlaceholder')"
                dense
                hide-details
                outlined
                class="flex-grow-1"
              />
              <span v-else class="flex-grow-1 text-body-2">
                {{ reason.text }}
              </span>

              <v-btn
                v-if="isEditing"
                icon
                small
                color="grey"
                @click="removeReason(index)"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </transition-group>
        </draggable>

        <!-- 空狀態提示 -->
        <v-alert
          v-if="isEditing && editableReasons.length === 0"
          type="info"
          dense
          text
          class="mt-2"
        >
          {{ $t('RescheduleSettings.willUseSystemDefault') }}
        </v-alert>

        <!-- 新增按鈕 -->
        <v-btn
          v-if="isEditing && editableReasons.length < maxReasons"
          text
          color="primary"
          class="mt-2"
          @click="addReason"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('RescheduleSettings.addOption') }}
        </v-btn>
        <p v-if="isEditing && editableReasons.length >= maxReasons" class="mt-2 text-caption grey--text">
          {{ $t('RescheduleSettings.maxOptions') }}
        </p>

        <p class="text-caption grey--text mt-4 mb-0">
          {{ $t('RescheduleSettings.currentCount', { count: editableReasons.length }) }}
        </p>
      </v-card-text>
    </v-card>

    <!-- 是否聯繫卡片 -->
    <v-card elevation="1" rounded="lg">
      <v-card-text class="pa-6">
        <div class="d-flex justify-space-between align-center mb-2">
          <h3 class="text-subtitle-1 font-weight-bold">
            {{ $t('RescheduleSettings.contactTitle') }}
          </h3>
          <div class="d-flex align-center">
            <v-chip
              v-if="isContactsUsingDefault && !isEditing"
              small
              color="grey lighten-2"
              text-color="grey darken-1"
            >
              <v-icon left small>mdi-information-outline</v-icon>
              {{ $t('RescheduleSettings.usingSystemDefault') }}
            </v-chip>
            <v-btn
              v-if="isEditing && !isContactsUsingDefault && editableContacts.length > 0"
              text
              small
              color="grey"
              @click="clearOptions('contacts')"
            >
              {{ $t('RescheduleSettings.resetToDefault') }}
            </v-btn>
          </div>
        </div>
        <p class="text-body-2 grey--text text--darken-1 mb-2">
          {{ $t('RescheduleSettings.contactDesc') }}
        </p>
        <p class="text-caption grey--text text--darken-1 mb-4">
          {{ $t('RescheduleSettings.contactNote') }}
        </p>

        <draggable
          v-model="editableContacts"
          :disabled="!isEditing"
          handle=".drag-handle"
          animation="200"
          class="options-list"
        >
          <transition-group type="transition" name="flip-list">
            <div
              v-for="(contact, index) in editableContacts"
              :key="contact.id || `contact-${index}`"
              class="option-item d-flex align-center pa-3 mb-2 rounded-lg"
              :class="{ 'editing': isEditing }"
            >
              <v-icon
                v-if="isEditing"
                class="drag-handle mr-3"
                color="grey"
              >
                mdi-drag
              </v-icon>
              <v-icon
                v-else
                class="mr-3"
                color="grey lighten-1"
              >
                mdi-circle-small
              </v-icon>

              <v-text-field
                v-if="isEditing"
                v-model="contact.text"
                :placeholder="$t('RescheduleSettings.inputPlaceholder')"
                dense
                hide-details
                outlined
                class="flex-grow-1"
              />
              <span v-else class="flex-grow-1 text-body-2">
                {{ contact.text }}
              </span>

              <v-btn
                v-if="isEditing"
                icon
                small
                color="grey"
                @click="removeContact(index)"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </transition-group>
        </draggable>

        <!-- 空狀態提示 -->
        <v-alert
          v-if="isEditing && editableContacts.length === 0"
          type="info"
          dense
          text
          class="mt-2"
        >
          {{ $t('RescheduleSettings.willUseSystemDefault') }}
        </v-alert>

        <!-- 新增按鈕 -->
        <v-btn
          v-if="isEditing && editableContacts.length < maxContacts"
          text
          color="primary"
          class="mt-2"
          @click="addContact"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('RescheduleSettings.addOption') }}
        </v-btn>
        <p v-if="isEditing && editableContacts.length >= maxContacts" class="mt-2 text-caption grey--text">
          {{ $t('RescheduleSettings.maxOptions') }}
        </p>

        <p class="text-caption grey--text mt-4 mb-0">
          {{ $t('RescheduleSettings.currentCount', { count: editableContacts.length }) }}
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import { getRescheduleOptionsAPI, updateRescheduleOptionsAPI } from '@/api'

export default {
  name: 'RescheduleOptionsEditor',

  components: {
    draggable
  },

  props: {
    // 是否隱藏內部的編輯控制按鈕（編輯/取消/儲存）
    // 當父元件想要自行控制編輯流程時設為 true
    hideEditControls: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      isEditing: false,
      maxReasons: 10,
      maxContacts: 4,
      // 原始資料（用於取消時還原）
      originalReasons: [],
      originalContacts: [],
      // 編輯中的資料
      editableReasons: [],
      editableContacts: [],
      // 用於生成唯一 ID
      idCounter: 0,
      // 是否使用系統預設（分開計算）
      isReasonsUsingDefault: false,
      isContactsUsingDefault: false,
      // 編輯前的狀態（用於儲存判斷）
      wasReasonsUsingDefault: false,
      wasContactsUsingDefault: false
    }
  },

  computed: {
    ...mapGetters(['axiosParamData']),

    // 當前選項資料（供父元件或預覽元件使用）
    currentOptions() {
      return {
        reasons: this.optionsToTextArray(this.editableReasons),
        contacts: this.optionsToTextArray(this.editableContacts)
      }
    }
  },

  watch: {
    // 監聽選項變化並發射事件（使用 debounce 優化）
    editableReasons: {
      handler() {
        if (this.isEditing) {
          this.debouncedEmitOptionsChange()
        }
      },
      deep: true
    },
    editableContacts: {
      handler() {
        if (this.isEditing) {
          this.debouncedEmitOptionsChange()
        }
      },
      deep: true
    }
  },

  created() {
    // 建立 debounced 版本的 emit 函式（200ms 延遲）
    this.debouncedEmitOptionsChange = debounce(this.emitOptionsChange, 200)
  },

  beforeDestroy() {
    // 清理 debounce timer 避免記憶體洩漏
    if (this.debouncedEmitOptionsChange) {
      this.debouncedEmitOptionsChange.cancel()
    }
  },

  mounted() {
    this.fetchOptions()
  },

  methods: {
    emitOptionsChange() {
      // 發射選項變化事件，供父元件即時更新預覽
      this.$emit('options-change', this.currentOptions)
    },

    generateId() {
      return `option-${Date.now()}-${this.idCounter++}`
    },

    // 將 API 回應轉換為內部格式（支援新舊格式）
    apiResponseToOptions(items) {
      if (!items || !Array.isArray(items) || items.length === 0) {
        return []
      }
      // 新格式：[{ id: "123", text: "..." }]
      if (typeof items[0] === 'object' && items[0].text) {
        return items.map(item => ({
          id: item.id || this.generateId(),
          text: item.text
        }))
      }
      // 舊格式（相容）：["text1", "text2"]
      return items.map(text => ({
        id: this.generateId(),
        text
      }))
    },

    // 將內部格式轉換為 API 請求格式
    // forceNoId: true 時不帶 id（全部當新增，用於從預設編輯的情況）
    // 否則：有 server id（數字字串）→ { id, text }，新增項目 → { text }
    optionsToApiFormat(options, forceNoId = false) {
      return options
        .filter(opt => opt.text && opt.text.trim())
        .map(opt => {
          // 如果是從預設編輯的，不帶 id
          if (forceNoId) {
            return { text: opt.text.trim() }
          }
          // 判斷是否為 server 給的 id（數字字串）
          const isServerId = /^\d+$/.test(opt.id)
          if (isServerId) {
            return { id: opt.id, text: opt.text.trim() }
          }
          // 新增的項目只傳 text
          return { text: opt.text.trim() }
        })
    },

    // 保留給 currentOptions computed（預覽用）
    optionsToTextArray(options) {
      return options.map(option => option.text).filter(text => text && text.trim())
    },

    // 判斷選項是否有改動
    hasChanges(original, edited) {
      if (original.length !== edited.length) return true
      return original.some((item, i) => item.text !== edited[i]?.text)
    },

    async fetchOptions() {
      this.loading = true
      try {
        const response = await getRescheduleOptionsAPI(this.axiosParamData)
        const data = response.data
        const systemDefaults = data.systemDefaults || {}

        // 判斷是否使用預設（分開計算）
        const hasCustomReasons = data.reasons && data.reasons.length > 0
        const hasCustomContacts = data.contacts && data.contacts.length > 0
        this.isReasonsUsingDefault = !hasCustomReasons
        this.isContactsUsingDefault = !hasCustomContacts

        // 處理改約原因：診所自訂 > 系統預設
        if (hasCustomReasons) {
          this.editableReasons = this.apiResponseToOptions(data.reasons)
        } else {
          this.editableReasons = this.apiResponseToOptions(systemDefaults.reasons)
        }

        // 處理是否聯繫：診所自訂 > 系統預設
        if (hasCustomContacts) {
          this.editableContacts = this.apiResponseToOptions(data.contacts)
        } else {
          this.editableContacts = this.apiResponseToOptions(systemDefaults.contacts)
        }

        this.saveOriginalData()
      } catch (error) {
        console.error('Failed to fetch reschedule options:', error)
        this.editableReasons = []
        this.editableContacts = []
        this.isReasonsUsingDefault = true
        this.isContactsUsingDefault = true
        this.saveOriginalData()
      } finally {
        this.loading = false
      }
    },

    saveOriginalData() {
      this.originalReasons = JSON.parse(JSON.stringify(this.editableReasons))
      this.originalContacts = JSON.parse(JSON.stringify(this.editableContacts))
    },

    startEditing() {
      this.saveOriginalData()
      // 記錄編輯前狀態（分開計算）
      this.wasReasonsUsingDefault = this.isReasonsUsingDefault
      this.wasContactsUsingDefault = this.isContactsUsingDefault
      this.isEditing = true
      this.$emit('editing-change', true)
      this.emitOptionsChange()
    },

    cancelEditing() {
      // 還原為原始資料
      this.editableReasons = JSON.parse(JSON.stringify(this.originalReasons))
      this.editableContacts = JSON.parse(JSON.stringify(this.originalContacts))
      this.isEditing = false
      this.$emit('editing-change', false)
      this.$emit('cancel')
    },

    async saveOptions() {
      // 過濾空選項
      const validReasons = this.editableReasons.filter(r => r.text && r.text.trim())
      const validContacts = this.editableContacts.filter(c => c.text && c.text.trim())

      // 檢查是否有改動
      const reasonsChanged = this.hasChanges(this.originalReasons, validReasons)
      const contactsChanged = this.hasChanges(this.originalContacts, validContacts)

      if (!reasonsChanged && !contactsChanged) {
        // 沒有改動，不送 API
        this.isEditing = false
        this.$emit('editing-change', false)
        return true
      }

      this.loading = true
      try {
        // 決定每個類別要送什麼資料
        let reasonsData, contactsData

        if (validReasons.length === 0) {
          // 刪光了 → 送空陣列（使用預設）
          reasonsData = []
        } else if (reasonsChanged) {
          // reasons 有改動，送新的內容
          reasonsData = this.optionsToApiFormat(validReasons, this.wasReasonsUsingDefault)
        } else if (this.wasReasonsUsingDefault) {
          // reasons 沒改動，原本是預設，送空陣列保持預設
          reasonsData = []
        } else {
          // reasons 沒改動，原本是自訂，送原來的內容
          reasonsData = this.optionsToApiFormat(this.originalReasons, false)
        }

        if (validContacts.length === 0) {
          // 刪光了 → 送空陣列（使用預設）
          contactsData = []
        } else if (contactsChanged) {
          // contacts 有改動，送新的內容
          contactsData = this.optionsToApiFormat(validContacts, this.wasContactsUsingDefault)
        } else if (this.wasContactsUsingDefault) {
          // contacts 沒改動，原本是預設，送空陣列保持預設
          contactsData = []
        } else {
          // contacts 沒改動，原本是自訂，送原來的內容
          contactsData = this.optionsToApiFormat(this.originalContacts, false)
        }

        const data = {
          ...this.axiosParamData,
          reasons: reasonsData,
          contacts: contactsData
        }

        await updateRescheduleOptionsAPI(data)

        // 重新讀取以正確設定狀態和取得 server id
        await this.fetchOptions()
        this.isEditing = false
        this.$emit('editing-change', false)
        this.$emit('save-success')
        return true
      } catch (error) {
        console.error('Failed to save reschedule options:', error)
        this.$notify.error(this.$t('RescheduleSettings.saveFailed'))
        this.$emit('save-error', error)
        return false
      } finally {
        this.loading = false
      }
    },

    addReason() {
      if (this.editableReasons.length < this.maxReasons) {
        this.editableReasons.push({
          id: this.generateId(),
          text: ''
        })
      }
    },

    async removeReason(index) {
      if (this.editableReasons.length === 1) {
        // 最後一個，顯示確認
        if (await this.$notify.confirm(this.$t('RescheduleSettings.confirmDeleteLast'))) {
          this.editableReasons = []
        }
      } else {
        this.editableReasons.splice(index, 1)
      }
    },

    addContact() {
      if (this.editableContacts.length < this.maxContacts) {
        this.editableContacts.push({
          id: this.generateId(),
          text: ''
        })
      }
    },

    async removeContact(index) {
      if (this.editableContacts.length === 1) {
        // 最後一個，顯示確認
        if (await this.$notify.confirm(this.$t('RescheduleSettings.confirmDeleteLast'))) {
          this.editableContacts = []
        }
      } else {
        this.editableContacts.splice(index, 1)
      }
    },

    // 清空選項（在編輯模式下，清空後會顯示「儲存後將使用系統預設」）
    async clearOptions(category) {
      if (!(await this.$notify.confirm(this.$t('RescheduleSettings.confirmResetToDefault')))) {
        return
      }
      if (category === 'reasons') {
        this.editableReasons = []
      } else if (category === 'contacts') {
        this.editableContacts = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
.reschedule-options-editor {
  position: relative;
  // 移除固定寬度和置中，讓父元件控制版面
  // 移除 padding，讓父元件控制間距
}

.options-list {
  min-height: 50px;
}

.option-item {
  background-color: #f5f5f5;
  transition: all 0.2s ease;

  &.editing {
    background-color: #fafafa;
    border: 1px solid #e0e0e0;

    .drag-handle {
      cursor: move;
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }
  }
}

.flip-list-move {
  transition: transform 0.3s;
}

.flip-list-enter-active,
.flip-list-leave-active {
  transition: all 0.3s;
}

.flip-list-enter,
.flip-list-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
